import { useEffect, useState, useCallback } from 'react';
import { useAppStore } from '@/store/appStore';
import { Play, Square, Clock, ChevronUp, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

export default function TimerWidget() {
  const {
    activeTimer,
    timerTask,
    elapsedSeconds,
    startTimer,
    stopTimer,
    projects,
    initializeTimer,
    currentOrg,
  } = useAppStore();

  const [isExpanded, setIsExpanded] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');

  const [assignedTasks, setAssignedTasks] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);

  // Initialize timer on mount - will restore from localStorage if active
  useEffect(() => {
    initializeTimer();
  }, [initializeTimer]);

  const loadAllMyTasks = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const { fetchAssignedTasks } = useAppStore.getState();
      const tasks = await fetchAssignedTasks(currentOrg.id);
      setAssignedTasks(tasks);

      // Extract unique projects from assigned tasks
      const projectIds = [...new Set(tasks.map(t => t.project_id))];
      const projList = projects.filter(p => projectIds.includes(p.id));
      setAvailableProjects(projList);
    } catch (err) {
      console.error('Failed to load assigned tasks:', err);
    }
  }, [projects, currentOrg]);

  useEffect(() => {
    loadAllMyTasks();
  }, [loadAllMyTasks]);

  const filteredTasks = assignedTasks.filter(t => t.project_id === selectedProjectId);

  // Keyboard shortcut Shift+T to toggle minimization
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.shiftKey && (e.key === 'T' || e.key === 't')) {
        e.preventDefault();
        setIsMinimized(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (activeTimer && timerTask) {
      setIsExpanded(true);
    }
  }, [activeTimer, timerTask]);

  const handleStartTimer = async () => {
    if (!selectedTaskId) {
      toast.error('Please select a task first');
      return;
    }
    try {
      await startTimer(selectedTaskId);
      toast.success('Timer started!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleStopTimer = async () => {
    try {
      await stopTimer();
      toast.success('Timer stopped!');
      setSelectedTaskId('');
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className={`fixed bottom-4 right-4 md:bottom-8 md:right-8 z-50 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center transition-all ${activeTimer ? 'bg-cyan-500 timer-glow-pulse' : 'bg-zinc-800 border border-zinc-700'
          }`}
        data-testid="timer-widget-minimized"
      >
        <Clock className={`w-5 h-5 md:w-6 md:h-6 ${activeTimer ? 'text-black' : 'text-cyan-400'}`} />
        {activeTimer && (
          <span className="absolute -top-1 -right-1 text-xs bg-zinc-900 text-cyan-400 px-1.5 py-0.5 rounded font-['JetBrains_Mono']">
            {formatTime(elapsedSeconds).slice(0, 5)}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-4 right-4 md:bottom-8 md:right-8 z-50 w-72 md:w-80 bg-zinc-900 border rounded-2xl shadow-2xl overflow-hidden transition-all ${activeTimer ? 'border-cyan-500/50 timer-glow' : 'border-zinc-800'
        }`}
      data-testid="timer-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 md:p-4 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <Clock className={`w-4 h-4 md:w-5 md:h-5 ${activeTimer ? 'text-cyan-400' : 'text-zinc-400'}`} />
          <span className="text-sm font-medium text-white">Time Tracker</span>
          {activeTimer && (
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-zinc-400 hover:text-white"
            data-testid="timer-expand-btn"
          >
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 text-zinc-400 hover:text-white"
            data-testid="timer-minimize-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Timer Display */}
      <div className="p-4 md:p-6 text-center">
        <p
          className={`text-3xl md:text-4xl font-bold font-['JetBrains_Mono'] tracking-wider ${activeTimer ? 'text-cyan-400' : 'text-zinc-500'
            }`}
          data-testid="timer-display"
        >
          {formatTime(elapsedSeconds)}
        </p>
        {activeTimer && timerTask && (
          <p className="text-sm text-zinc-400 mt-2 truncate" data-testid="timer-task-name">
            {timerTask.title}
          </p>
        )}
      </div>

      {/* Controls */}
      {isExpanded && (
        <div className="p-3 md:p-4 pt-0 space-y-3">
          {!activeTimer && (
            <>
              <Select value={selectedProjectId} onValueChange={(v) => {
                setSelectedProjectId(v);
                setSelectedTaskId('');
              }}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="timer-project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-300">
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                  {availableProjects.length === 0 && (
                    <div className="p-2 text-xs text-zinc-500 text-center">No projects with assigned tasks</div>
                  )}
                </SelectContent>
              </Select>

              <Select value={selectedTaskId} onValueChange={setSelectedTaskId} disabled={!selectedProjectId}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="timer-task-select">
                  <SelectValue placeholder="Select task" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-300">
                  {filteredTasks.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}

          <div className="flex gap-2">
            {activeTimer ? (
              <Button
                onClick={handleStopTimer}
                className="flex-1 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
                data-testid="timer-stop-btn"
              >
                <Square className="w-4 h-4 mr-2 fill-current" /> Stop
              </Button>
            ) : (
              <Button
                onClick={handleStartTimer}
                disabled={!selectedTaskId}
                className="flex-1 bg-cyan-500 text-black hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="timer-start-btn"
              >
                <Play className="w-4 h-4 mr-2 fill-current" /> Start
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
